import type {
  Invoice,
  HaulierLineItem,
  WarehouseLineItem,
  WarehouseSummary,
  ReturnsSummary,
  ZoneSummary,
} from "../types";
import {
  InvoiceType,
  HaulierLineStatus,
  WarehouseLineStatus,
} from "../types";

// ─── CSV Helpers ────────────────────────────────────────────────────────────

function escapeField(value: string | number | null | undefined): string {
  const str = value == null ? "" : String(value);
  // Wrap in quotes if the value contains commas, quotes, or newlines
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function toCsvRow(fields: (string | number | null | undefined)[]): string {
  return fields.map(escapeField).join(",");
}

/** Format an ISO date string (YYYY-MM-DD or full ISO) as DD/MM/YYYY */
function formatDate(isoDate: string): string {
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return isoDate;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/** Format a number as £X.XX */
function formatCurrency(amount: number): string {
  return `\u00A3${amount.toFixed(2)}`;
}

/** Today's date as DD/MM/YYYY */
function todayFormatted(): string {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// ─── Haulier Export ─────────────────────────────────────────────────────────

const HAULIER_HEADERS = [
  "Con No",
  "Date",
  "Destination",
  "Postcode",
  "Service",
  "Pallet Size",
  "Qty",
  "Invoiced",
  "Expected",
  "Difference",
  "Status",
  "Return",
  "Notes",
];

function haulierLineToCsv(line: HaulierLineItem): string {
  return toCsvRow([
    line.consignment_number,
    line.delivery_date,
    line.destination_name,
    line.destination_postcode,
    line.service_type,
    line.pallet_spec,
    line.pallet_quantity,
    line.invoiced_amount.toFixed(2),
    line.expected_amount.toFixed(2),
    line.amount_difference.toFixed(2),
    line.status,
    line.is_return ? "RETURN" : "",
    line.notes,
  ]);
}

const HAULIER_ISSUE_STATUSES: Set<string> = new Set([
  HaulierLineStatus.OVERCHARGED,
  HaulierLineStatus.UNDERCHARGED,
  HaulierLineStatus.RATE_NOT_FOUND,
  HaulierLineStatus.UNKNOWN_SERVICE,
]);

// ─── Warehouse Export ───────────────────────────────────────────────────────

const WAREHOUSE_HEADERS = [
  "Charge ID",
  "Date",
  "Type",
  "Code",
  "Product",
  "Storage Unit",
  "Invoiced",
  "Expected",
  "Difference",
  "Status",
  "Notes",
];

function warehouseLineToCsv(line: WarehouseLineItem): string {
  return toCsvRow([
    line.charge_id,
    line.created_at,
    line.charge_type,
    line.charge_code,
    line.product_code,
    line.storage_unit_reference,
    line.invoiced_price.toFixed(2),
    line.expected_price.toFixed(2),
    line.price_difference.toFixed(2),
    line.status,
    line.notes,
  ]);
}

const WAREHOUSE_ISSUE_STATUSES: Set<string> = new Set([
  WarehouseLineStatus.OVERCHARGED,
  WarehouseLineStatus.UNDERCHARGED,
  WarehouseLineStatus.DUPLICATE,
]);

// ─── Report Sections ────────────────────────────────────────────────────────

interface StatusCounts {
  correct: number;
  overcharged: number;
  undercharged: number;
  rateNotFound: number;
  duplicate: number;
  totalIssueInvoiced: number;
  totalIssueExpected: number;
}

function countHaulierStatuses(
  lines: HaulierLineItem[],
  issueLines: HaulierLineItem[],
): StatusCounts {
  const counts: StatusCounts = {
    correct: 0,
    overcharged: 0,
    undercharged: 0,
    rateNotFound: 0,
    duplicate: 0,
    totalIssueInvoiced: 0,
    totalIssueExpected: 0,
  };
  for (const line of lines) {
    switch (line.status) {
      case HaulierLineStatus.CORRECT:
        counts.correct++;
        break;
      case HaulierLineStatus.OVERCHARGED:
        counts.overcharged++;
        break;
      case HaulierLineStatus.UNDERCHARGED:
        counts.undercharged++;
        break;
      case HaulierLineStatus.RATE_NOT_FOUND:
        counts.rateNotFound++;
        break;
      case HaulierLineStatus.DUPLICATE:
        counts.duplicate++;
        break;
      case HaulierLineStatus.UNKNOWN_SERVICE:
        // Unknown service is counted as rate-not-found for summary purposes
        counts.rateNotFound++;
        break;
    }
  }
  for (const line of issueLines) {
    counts.totalIssueInvoiced += line.invoiced_amount;
    counts.totalIssueExpected += line.expected_amount;
  }
  return counts;
}

function countWarehouseStatuses(
  lines: WarehouseLineItem[],
  issueLines: WarehouseLineItem[],
): StatusCounts {
  const counts: StatusCounts = {
    correct: 0,
    overcharged: 0,
    undercharged: 0,
    rateNotFound: 0,
    duplicate: 0,
    totalIssueInvoiced: 0,
    totalIssueExpected: 0,
  };
  for (const line of lines) {
    switch (line.status) {
      case WarehouseLineStatus.CORRECT:
        counts.correct++;
        break;
      case WarehouseLineStatus.OVERCHARGED:
        counts.overcharged++;
        break;
      case WarehouseLineStatus.UNDERCHARGED:
        counts.undercharged++;
        break;
      case WarehouseLineStatus.DUPLICATE:
        counts.duplicate++;
        break;
    }
  }
  for (const line of issueLines) {
    counts.totalIssueInvoiced += line.invoiced_price;
    counts.totalIssueExpected += line.expected_price;
  }
  return counts;
}

function buildHeader(
  invoice: Invoice,
  totalLines: number,
  issueCount: number,
): string[] {
  return [
    "PRICING VERIFICATION REPORT",
    toCsvRow(["Invoice Number", invoice.invoice_number]),
    toCsvRow(["Supplier", invoice.supplier]),
    toCsvRow(["Invoice Date", formatDate(invoice.invoice_date)]),
    toCsvRow(["Prepared By", "Zebra Renewables"]),
    toCsvRow(["Report Date", todayFormatted()]),
    toCsvRow(["Total Lines Checked", totalLines]),
    toCsvRow(["Lines With Issues", issueCount]),
  ];
}

/** Build a warehouse-specific charge type breakdown. */
function buildWarehouseChargeTypeSummary(lines: WarehouseLineItem[]): string[] {
  const totals: Record<string, { count: number; total: number }> = {};
  for (const line of lines) {
    const ct = line.charge_type || "UNKNOWN";
    if (!totals[ct]) totals[ct] = { count: 0, total: 0 };
    totals[ct].count += 1;
    totals[ct].total += line.invoiced_price;
  }

  const LABELS: Record<string, string> = {
    PICK: "Goods Out (Pick)",
    RECEIPT: "Goods In (Receipt)",
    RENTAL: "Weekly Storage (Rental)",
  };

  const rows: string[] = ["", "CHARGE TYPE BREAKDOWN"];
  rows.push(toCsvRow(["Charge Type", "Lines", "Total Invoiced"]));

  let grandTotal = 0;
  for (const [type, data] of Object.entries(totals)) {
    rows.push(toCsvRow([LABELS[type] || type, data.count, formatCurrency(data.total)]));
    grandTotal += data.total;
  }
  rows.push(toCsvRow(["TOTAL", lines.length, formatCurrency(grandTotal)]));
  return rows;
}

function buildSummary(counts: StatusCounts): string[] {
  const netOvercharge = counts.totalIssueInvoiced - counts.totalIssueExpected;
  const label = netOvercharge >= 0 ? "Net Overcharge" : "Net Undercharge";
  return [
    "SUMMARY",
    toCsvRow([
      "Total Invoiced (Issues Only)",
      formatCurrency(counts.totalIssueInvoiced),
    ]),
    toCsvRow([
      "Total Expected (Issues Only)",
      formatCurrency(counts.totalIssueExpected),
    ]),
    toCsvRow([label, formatCurrency(Math.abs(netOvercharge))]),
    toCsvRow(["Correct Lines", counts.correct]),
    toCsvRow(["Overcharged Lines", counts.overcharged]),
    toCsvRow(["Undercharged Lines", counts.undercharged]),
    toCsvRow(["Rate Not Found", counts.rateNotFound]),
    toCsvRow(["Duplicate Lines", counts.duplicate]),
  ];
}

/** Build a WAREHOUSE SUMMARY section from the warehouse_summary field. */
function buildWarehouseSummarySection(ws: WarehouseSummary): string[] {
  return [
    "",
    "WAREHOUSE SUMMARY",
    toCsvRow(["Type", "Count", "Total"]),
    toCsvRow(["RH&D Out", ws.pick_count, formatCurrency(ws.pick_total)]),
    toCsvRow(["RH&D In", ws.receipt_count, formatCurrency(ws.receipt_total)]),
    toCsvRow(["Storage", ws.rental_count, formatCurrency(ws.rental_total)]),
  ];
}

/** Build a ZONE SUMMARY section for haulier invoices. */
function buildZoneSummarySection(zones: ZoneSummary[]): string[] {
  const rows: string[] = ["", "ZONE SUMMARY"];
  rows.push(
    toCsvRow(["Zone", "Pallet Type", "Count", "Per Pallet Overcharge", "Total Difference"]),
  );

  for (const zone of zones) {
    for (const pb of zone.pallet_breakdown) {
      rows.push(
        toCsvRow([
          zone.zone,
          pb.pallet_type,
          pb.count,
          formatCurrency(pb.per_unit_difference),
          formatCurrency(pb.difference),
        ]),
      );
    }
    // Zone totals row
    rows.push(
      toCsvRow([
        `Zone ${zone.zone} Total`,
        "",
        zone.lines,
        "",
        formatCurrency(zone.difference),
      ]),
    );
  }

  return rows;
}

/** Build a RETURNS SUMMARY section for haulier invoices. */
function buildReturnsSummarySection(rs: ReturnsSummary): string[] {
  return [
    "",
    "RETURNS SUMMARY",
    toCsvRow(["Return Consignments", rs.count]),
    toCsvRow(["Total Return Cost", formatCurrency(rs.total_cost)]),
  ];
}

// ─── Public API ─────────────────────────────────────────────────────────────

export function exportInvoiceCsv(
  invoice: Invoice,
  haulierLines: HaulierLineItem[],
  warehouseLines: WarehouseLineItem[],
): void {
  const sections: string[] = [];

  if (invoice.invoice_type === InvoiceType.HAULIER) {
    const allLines = haulierLines;
    const issueLines = allLines.filter((l) =>
      HAULIER_ISSUE_STATUSES.has(l.status),
    );
    const counts = countHaulierStatuses(allLines, issueLines);

    // Header
    sections.push(
      ...buildHeader(invoice, allLines.length, issueLines.length),
    );
    sections.push(",");

    // Discrepancy lines
    sections.push("DISCREPANCY LINES");
    sections.push(toCsvRow(HAULIER_HEADERS));
    for (const line of issueLines) {
      sections.push(haulierLineToCsv(line));
    }
    sections.push(",");

    // All line items
    sections.push("ALL LINE ITEMS");
    sections.push(toCsvRow(HAULIER_HEADERS));
    for (const line of allLines) {
      sections.push(haulierLineToCsv(line));
    }
    sections.push(",");

    // Summary
    sections.push(...buildSummary(counts));

    // Zone summary (haulier only)
    if (invoice.zone_summary && invoice.zone_summary.length > 0) {
      sections.push(...buildZoneSummarySection(invoice.zone_summary));
    }

    // Returns summary (haulier only)
    if (invoice.returns_summary && invoice.returns_summary.count > 0) {
      sections.push(...buildReturnsSummarySection(invoice.returns_summary));
    }
  } else {
    const allLines = warehouseLines;
    const issueLines = allLines.filter((l) =>
      WAREHOUSE_ISSUE_STATUSES.has(l.status),
    );
    const counts = countWarehouseStatuses(allLines, issueLines);

    // Header
    sections.push(
      ...buildHeader(invoice, allLines.length, issueLines.length),
    );
    sections.push(",");

    // Discrepancy lines
    sections.push("DISCREPANCY LINES");
    sections.push(toCsvRow(WAREHOUSE_HEADERS));
    for (const line of issueLines) {
      sections.push(warehouseLineToCsv(line));
    }
    sections.push(",");

    // All line items
    sections.push("ALL LINE ITEMS");
    sections.push(toCsvRow(WAREHOUSE_HEADERS));
    for (const line of allLines) {
      sections.push(warehouseLineToCsv(line));
    }
    sections.push(",");

    // Summary
    sections.push(...buildSummary(counts));

    // Warehouse-specific charge type breakdown
    sections.push(...buildWarehouseChargeTypeSummary(allLines));

    // Warehouse summary (from backend aggregation)
    if (invoice.warehouse_summary) {
      sections.push(...buildWarehouseSummarySection(invoice.warehouse_summary));
    }
  }

  // Prepend UTF-8 BOM so Excel reads £ signs correctly
  const BOM = "\uFEFF";
  const csv = BOM + sections.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `${invoice.invoice_number || invoice.id}_verification_report.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
